import { useState, useEffect, useMemo } from 'react';
import { useNavigation } from '../../contexts/NavigationContext';
import { useSession } from '../../contexts/SessionContext';
import { getConnections, deleteConnection } from '../../services/storage/connectionStorage';
import { loadLatestMessages } from '../../services/chat/messageStore';
import {
  reconcileDeletedMarks,
  applyDeletedMarkChange,
  getLeftConnectionIds,
  saveReceivedAnonData,
} from '../../services/storage/userDataStorage';
import { getConnectionProfiles } from '../../services/activeSession';
import { getSharedItems } from '../../services/sharing/connectionSharing';
import { logConnectionDeleted } from '../../services/analytics/eventLogger';
import { formatRelativeTime } from '../../utils/dateHelpers';
import { getSenderId } from '../../utils/identityUtils';
import { getInactiveStatus } from '../../utils/anonInactiveStatus';
import { hasData } from '../../utils/objectHelpers';
import { formatLocationForCard } from '../../utils/locationFormatters';
import {
  hasUnreadMessages,
  getTime,
  deriveLatestMessage,
  hasChatMessages,
  formatMessagePreview,
} from '../../utils/chatHelpers';
import SwipeableConnectionCard from '../../components/UI/Cards/SwipeableConnectionCard';
import { clearBadge } from '../../services/pushNotificationService';
import { useConnectionContext } from '../../contexts/ConnectionStateContext';
import { enrichPeerAnonData } from '../../utils/connectionName';

const CHATS_LAST_VIEWED_KEY = 'chats_sort_last_viewed';

/**
 * ChatListPage Component
 *
 * Dedicated screen for the Chats tab. Shows connections with chat messages,
 * sorted by most recent message.
 */
const ChatListPage = () => {
  const { navigateTo } = useNavigation();
  const { updateSessionChatRoomId } = useSession();
  const { updateAnonData } = useConnectionContext();

  const [connections, setConnections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // "No chats yet" is only trustworthy after the latest-messages enrichment
  // settles — rooms with no local message history can't qualify before it.
  const [enriched, setEnriched] = useState(false);
  const [enrichFailed, setEnrichFailed] = useState(false);

  // Mark chats as viewed on mount — clears unread indicator and PWA badge
  useEffect(() => {
    window.scrollTo(0, 0);
    localStorage.setItem(CHATS_LAST_VIEWED_KEY, new Date().toISOString());
    localStorage.setItem('has_unread_chats', '');
    clearBadge();
  }, []);

  // Filter to chat connections and sort by recency.
  // A DB-sourced latestMessage is always a display message (chat, share line,
  // or banner) — any of them qualifies, matching hasChatMessages' local
  // semantics. The old `type === 'chat'` check hid recovered rooms on a fresh
  // device whenever the newest message was a share line or re-meet banner.
  const chatConnections = useMemo(() => {
    const result = connections.filter(
      (c) =>
        !c.isManualContact &&
        (hasChatMessages(c.chatMessages) || !!c.latestMessage) &&
        (getInactiveStatus(c) === null || getInactiveStatus(c) === 'active')
    );
    result.sort(
      (a, b) => getTime(b.latestMessage?.timestamp) - getTime(a.latestMessage?.timestamp)
    );
    return result;
  }, [connections]);

  // Load connections from localStorage
  const loadConnectionsFromStorage = async () => {
    const allConnections = await getConnections();
    if (!allConnections || !allConnections.length) {
      setConnections([]);
      setIsLoading(false);
      return [];
    }

    const validConnections = allConnections
      .filter((conn) => {
        if (hasData(conn.manualContactData)) {
          const name = conn.manualContactData.name || '';
          return name.trim().length > 0;
        }
        return true;
      })
      .map((conn) => {
        if (hasData(conn.manualContactData)) {
          return {
            chatRoomId: conn.chatRoomId,
            anonData: conn.manualContactData,
            connectionMetadata: conn.connectionMetadata || {},
            chatMessages: [],
            notes: { text: conn.notes || '' },
            hasSharedContactInfo: Boolean(
              conn.manualContactData?.phone ||
                conn.manualContactData?.email ||
                conn.manualContactData?.instagram
            ),
            isManualContact: true,
          };
        }
        const dataSource = hasData(conn.anonData) ? conn.anonData : {};
        return {
          chatRoomId: conn.chatRoomId,
          anonData: dataSource,
          displayName: conn.displayName || null,
          connectionMetadata: conn.connectionMetadata || {},
          chatMessages: conn.chatMessages || [],
          latestMessage: deriveLatestMessage(conn.chatMessages),
          notes: { text: conn.notes || '' },
          hasSharedContactInfo: Boolean(
            dataSource?.phone || dataSource?.email || dataSource?.instagram
          ),
          isManualContact: false,
        };
      });

    setConnections([...validConnections]);
    setIsLoading(false);
    return validConnections;
  };

  // Enrich connections from Supabase
  const enrichConnections = (validConnections) => {
    if (!validConnections || validConnections.length === 0) {
      setEnriched(true);
      return;
    }
    const chatRoomIds = validConnections.filter((c) => !c.isManualContact).map((c) => c.chatRoomId);
    const profileUserId = localStorage.getItem('profile_user_id');

    // Profile lookup — resolve current names/photos for converted users; plus
    // the live-sharing resolver (D2), which wholesale-replaces each peer's shared
    // items. sharedByRoom is null when unreadable (flag off / offline) → cache stands.
    if (profileUserId) {
      Promise.all([getConnectionProfiles(profileUserId), getSharedItems(profileUserId)])
        .then(([profiles, sharedByRoom]) => {
          const hasProfiles = profiles && profiles.length > 0;
          // sharedByRoom non-null only when the flag is on AND the read succeeded.
          if (!hasProfiles && sharedByRoom == null) return;
          const profileMap = {};
          for (const p of profiles || []) {
            profileMap[p.chat_room_id] = p;
          }
          setConnections((prev) =>
            prev.map((conn) => {
              if (conn.isManualContact) return conn;
              const updatedAnonData = enrichPeerAnonData(
                conn.anonData,
                profileMap[conn.chatRoomId],
                sharedByRoom,
                conn.chatRoomId
              );
              if (!updatedAnonData) return conn;
              updateAnonData(conn.chatRoomId, updatedAnonData);
              return { ...conn, anonData: updatedAnonData };
            })
          );
        })
        .catch((err) => console.error('[CHAT_LIST] Profile lookup failed:', err));
    }

    // Latest messages — for sort order and preview text
    if (chatRoomIds.length > 0) {
      loadLatestMessages(chatRoomIds)
        .then((latestMessages) => {
          setConnections((prev) =>
            prev.map((conn) => ({
              ...conn,
              latestMessage: latestMessages[conn.chatRoomId] || conn.latestMessage || null,
            }))
          );
          setEnriched(true);
        })
        .catch((err) => {
          console.error('[CHAT_LIST] Latest messages failed:', err);
          setEnrichFailed(true);
          setEnriched(true);
        });
    } else {
      setEnriched(true);
    }

    // Deleted check: the deleted mark follows the server both ways —
    // set for a confirmed-gone peer, cleared for one confirmed to exist (one read)
    reconcileDeletedMarks(validConnections)
      .then((changes) => {
        if (changes.deleted.length === 0 && changes.revived.length === 0) return;
        setConnections((prev) =>
          prev.map((conn) => {
            const anonData = applyDeletedMarkChange(conn, changes);
            return anonData ? { ...conn, anonData } : conn;
          })
        );
      })
      .catch((err) => console.error('[CHAT_LIST] Deleted check failed:', err));

    // Peer-left check (CD #15) — the peer left this connection / their account's
    // side of it; latch peerLeft so my copy freezes + becomes editable, exactly
    // like a deleted-account peer (isPeerGone). They are not told.
    getLeftConnectionIds(validConnections, profileUserId)
      .then((leftChatRoomIds) => {
        if (leftChatRoomIds.length === 0) return;
        setConnections((prev) =>
          prev.map((conn) => {
            if (!leftChatRoomIds.includes(conn.chatRoomId)) return conn;
            const updatedAnonData = { ...conn.anonData, peerLeft: true };
            saveReceivedAnonData(conn.chatRoomId, updatedAnonData);
            return { ...conn, anonData: updatedAnonData };
          })
        );
      })
      .catch((err) => console.error('[CHAT_LIST] Peer-left check failed:', err));
  };

  // Load on mount
  useEffect(() => {
    const init = async () => {
      const validConnections = await loadConnectionsFromStorage();
      enrichConnections(validConnections);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle clicking on a chat — opens conversation
  const handleChatClick = (chatRoomId) => {
    updateSessionChatRoomId(chatRoomId);
    navigateTo('profile-connection-page', {
      skipInitialOverlay: true,
      source: 'chat-list',
    });
  };

  // Handle clicking profile (image area) — opens detail page
  const handleProfileClick = (chatRoomId) => {
    updateSessionChatRoomId(chatRoomId);
    navigateTo('profile-connection-detail', {
      source: 'chat-list',
    });
  };

  // Handle deleting a connection. Per-side (CD #15): deleteConnection marks our
  // own side via leave_connection and removes only our rows — the other person
  // keeps everything and is not told.
  const handleDeleteConnection = async (chatRoomId) => {
    const deleted = await deleteConnection(chatRoomId);
    if (deleted) {
      // Track WHERE the delete happened (growth.md §47) — Chats-list swipe.
      logConnectionDeleted('chats', chatRoomId);
      setConnections((prev) => prev.filter((conn) => conn.chatRoomId !== chatRoomId));
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: '#FFFFFF',
          zIndex: 100,
        }}
      >
        <div style={{ height: 'env(safe-area-inset-top)', backgroundColor: '#FFFFFF' }} />
        <div
          style={{
            width: '100%',
            maxWidth: '600px',
            margin: '0 auto',
            boxSizing: 'border-box',
            paddingTop: '12px',
            paddingBottom: '12px',
            paddingLeft: '20px',
            paddingRight: '20px',
            textAlign: 'left',
          }}
        >
          <span
            style={{
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
              fontSize: '32px',
              fontWeight: '800',
              color: '#000000',
            }}
          >
            Chats
          </span>
        </div>
      </div>

      {/* Scrollable Content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingTop: '80px',
          paddingBottom: '80px',
        }}
      >
        {isLoading || (!enriched && chatConnections.length === 0) ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>
        ) : chatConnections.length === 0 ? (
          <div
            style={{
              padding: '40px 20px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              paddingTop: '30vh',
              minHeight: 'calc(100vh - 160px)',
            }}
          >
            <div style={{ fontSize: '16px', color: '#666666' }}>
              {enrichFailed ? 'Trouble loading your chats — check your connection' : 'No chats yet'}
            </div>
          </div>
        ) : (
          <div
            style={{
              padding: '0 0 20px 0',
              width: '100%',
              maxWidth: '600px',
              margin: '0 auto',
              boxSizing: 'border-box',
            }}
          >
            {chatConnections.map((connection, index) => {
              let latestMessage = connection.latestMessage || null;
              // PXP connections have per-user system messages — show ours, not
              // theirs. Pick the LATEST own banner: rooms hold one banner per
              // meeting since pxp-reconnection, so first-match showed the
              // formation banner (old venue + date) after a re-meet.
              if (latestMessage?.type === 'system') {
                const myId = getSenderId();
                const ownSystemMsgs = (connection.chatMessages || []).filter(
                  (msg) =>
                    msg.type === 'system' &&
                    (msg.id?.endsWith('_' + myId) || msg.id === 'system_' + connection.chatRoomId)
                );
                latestMessage = deriveLatestMessage(ownSystemMsgs) || latestMessage;
              }
              const { recentMessage, isMessageFromUser } = formatMessagePreview(
                latestMessage,
                getSenderId()
              );
              const chatTime = latestMessage?.timestamp || null;

              return (
                <SwipeableConnectionCard
                  key={connection.chatRoomId}
                  connection={connection}
                  onDelete={() => handleDeleteConnection(connection.chatRoomId)}
                  onProfileClick={() => handleProfileClick(connection.chatRoomId)}
                  onChatClick={() => handleChatClick(connection.chatRoomId)}
                  onCardClick={() => handleChatClick(connection.chatRoomId)}
                  getFormattedLocation={formatLocationForCard}
                  showMarginBottom={index < chatConnections.length - 1}
                  recentMessage={recentMessage}
                  isMessageFromUser={isMessageFromUser}
                  isManualContact={false}
                  hasUnreadMessage={hasUnreadMessages(
                    latestMessage ? [latestMessage] : [],
                    connection.chatRoomId
                  )}
                  displayTimestamp={formatRelativeTime(chatTime)}
                  variant="chat"
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatListPage;
